/*
 * Copyright (c) 2026. Bluemate B.V.
 */

import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router';
import {ResponseData} from '@dvrd/dvr-controls';
import {AppContext} from '../../misc/appContext';
import {isPWASupported, isStandalone} from '../../../util/pwaUtil';
import {usePushSubscription} from '../../../hooks/usePushSubscription';
import {useSnooze} from '../../../hooks/useSnooze';
import {listDeviceTokens} from '../../../util/requestSender';
import {NOTIFICATIONS_ROUTE} from '../../../util/constants';
import {SnoozeConfig} from '../../../util/snoozeUtil';
import NotificationModel from '../../../models/notificationModel';
import {
    isCurrentDevice,
    PushDeviceLike,
    readNotificationChannels,
    readNotificationPushDeviceIds
} from '../../../util/pushChannelUtil';
import FirstRunPushPrompt from './firstRunPushPrompt';

const FIRST_RUN_SNOOZE_FEATURE = 'push-first-run';
// Permanent per device+user — declining must never re-prompt (review decision on !293).
const FIRST_RUN_SNOOZE_COOLDOWNS = [Number.POSITIVE_INFINITY];

let dismissedThisSession = false;

/** This browser's push endpoint, or null when it has no subscription at all. */
async function getCurrentEndpoint(): Promise<string | null> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
    try {
        // getRegistration (not .ready) — .ready can hang forever on iOS
        // standalone PWAs even with a usable registration. Mirrors
        // pushChannelsContext.
        const registration = await navigator.serviceWorker.getRegistration('/');
        const subscription = (await registration?.pushManager.getSubscription()) ?? null;
        return subscription?.endpoint ?? null;
    } catch {
        return null;
    }
}

function fetchDevices(accountId: string): Promise<Array<PushDeviceLike>> {
    return new Promise(resolve => {
        listDeviceTokens({
            account_id: accountId,
            callback: (data: ResponseData) => {
                const rows = (data as {tokens?: Array<PushDeviceLike>; device_tokens?: Array<PushDeviceLike>}) || {};
                resolve(data.success ? (rows.tokens ?? rows.device_tokens ?? []) : []);
            },
            errorCallback: () => resolve([])
        });
    });
}

/** True when push actually reaches THIS device: it holds a live subscription
 *  registered on the account AND at least one notification pushes to it
 *  (targeting `null` = every device, so that counts). Deliberately NOT "does
 *  the account use push anywhere" — a second phone, or this one before the
 *  channels select was pointed at it, must still get the offer. */
async function isPushEnabledOnThisDevice(accountId: string, notifications: Array<NotificationModel>): Promise<boolean> {
    const endpoint = await getCurrentEndpoint();
    if (!endpoint) return false;
    const devices = await fetchDevices(accountId);
    const currentDevice = devices.find(device => isCurrentDevice(device, endpoint));
    if (!currentDevice) return false;
    return notifications.some(notification => {
        if (!readNotificationChannels(notification).includes('WEB_PUSH')) return false;
        const deviceIds = readNotificationPushDeviceIds(notification);
        return deviceIds === null || deviceIds.includes(currentDevice.id);
    });
}

export default function FirstRunPushPromptController() {
    const context = useContext(AppContext);
    const user = context.userContext.user;
    const account = context.accountContext.account;

    const [eligible, setEligible] = useState(false);

    const snoozeConfig = useMemo<SnoozeConfig | null>(
        () =>
            user ? {featureKey: FIRST_RUN_SNOOZE_FEATURE, scope: user.id, cooldowns: FIRST_RUN_SNOOZE_COOLDOWNS} : null,
        [user]
    );

    const {suppressed} = useSnooze(snoozeConfig);

    useEffect(() => {
        if (dismissedThisSession) return;
        if (!user || !account || suppressed || !isStandalone() || !isPWASupported()) {
            setEligible(false);
            return;
        }
        if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
            setEligible(false);
            return;
        }
        let cancelled = false;
        const accountId = account.id;
        NotificationModel.getAll(accountId, (notifications: Array<NotificationModel>, success: boolean) => {
            if (cancelled || !success) return;
            void isPushEnabledOnThisDevice(accountId, notifications).then(enabledHere => {
                if (!cancelled) setEligible(!enabledHere);
            });
        });
        return () => {
            cancelled = true;
        };
    }, [user, account, suppressed]);

    if (!eligible || !user || !account || !snoozeConfig) return null;

    return <FirstRunPushPromptActive userId={user.id} accountId={account.id} snooze={snoozeConfig} />;
}

interface ActiveProps {
    userId: string;
    accountId: string;
    snooze: SnoozeConfig;
}

function FirstRunPushPromptActive(props: ActiveProps) {
    const {userId, accountId, snooze} = props;
    const navigate = useNavigate();
    const {enable} = usePushSubscription(userId, accountId);
    const [active, setActive] = useState(true);
    const [loading, setLoading] = useState(false);

    async function onConfirm() {
        if (loading) return;
        setLoading(true);
        await enable().catch(() => undefined);
        setLoading(false);
        dismissedThisSession = true;
        setActive(false);
        navigate(NOTIFICATIONS_ROUTE, {state: {firstRunPushEnable: true}});
    }

    function onCancel() {
        dismissedThisSession = true;
        setActive(false);
    }

    return (
        <FirstRunPushPrompt
            active={active}
            loading={loading}
            snooze={snooze}
            onConfirm={onConfirm}
            onCancel={onCancel}
        />
    );
}
